import React, { memo, useCallback } from 'react'
import { ICellRendererParams } from 'ag-grid-community'
import { ProgressIcon } from '../../../../containers/commons/AgGrid/components/cell/custom/wbsItemStatus/ProgressIcon'
import { ProjectPlanNewRow } from '../../projectPlanNew'
import { colorPalette } from '../../../../style/colorPalette'
import { WbsItemStatus } from '../../../../../domain/entity/WbsItemEntity'
import { getWbsItemStatusLabel } from '../../../../../lib/functions/wbsItem'
import { styled } from '@mui/material'
import { RootDiv } from '../../../../containers/commons/AgGrid/components/cell/CellRendererRoot'

// CellEditor用のエクスポート（MUIスタイル - 編集時のみ使用）
export const ProjectPlanNewStatusRootDiv = styled(RootDiv)({
  '&.sevend-ag-cell': {
    padding: 0,
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    gap: '4px',
  },
})

export const ProgressIconArea = styled('span')({
  width: 20,
  display: 'flex',
  justifyContent: 'center',
  flexShrink: 0,
})

// インラインスタイルで軽量化（styled-componentsのオーバーヘッドを回避）
const rootStyle: React.CSSProperties = {
  padding: 0,
  margin: 0,
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  gap: '4px',
}

const progressIconAreaStyle: React.CSSProperties = {
  width: 20,
  display: 'flex',
  justifyContent: 'center',
  flexShrink: 0,
}

const iconButtonStyle: React.CSSProperties = {
  padding: '2px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

// ステータスごとのスタイルを事前計算（レンダリング時の計算を削減）
const statusStyles: Record<string, React.CSSProperties> = {
  [WbsItemStatus.TODO]: {
    backgroundColor: colorPalette.monotone[1],
    color: colorPalette.monotone[4],
    border: `1px solid ${colorPalette.monotone[2]}`,
  },
  [WbsItemStatus.DOING]: {
    backgroundColor: colorPalette.yellow[1],
    color: colorPalette.yellow[4],
    border: `1px solid ${colorPalette.yellow[2]}`,
  },
  [WbsItemStatus.REVIEW]: {
    backgroundColor: colorPalette.green[1],
    color: colorPalette.green[7],
    border: `1px solid ${colorPalette.green[2]}`,
  },
  [WbsItemStatus.DONE]: {
    backgroundColor: colorPalette.skyBlue[1],
    color: colorPalette.skyBlue[7],
    border: `1px solid ${colorPalette.skyBlue[2]}`,
  },
  [WbsItemStatus.DISCARD]: {
    backgroundColor: colorPalette.monotone[3],
    color: colorPalette.monotone[0],
    border: `1px solid ${colorPalette.monotone[3]}`,
  },
}

const defaultStatusStyle: React.CSSProperties = {
  backgroundColor: colorPalette.monotone[0],
  color: colorPalette.monotone[10],
  border: `1px solid ${colorPalette.monotone[10]}`,
}

const statusButtonBaseStyle: React.CSSProperties = {
  display: 'flex',
  minWidth: '77px',
  height: '22px',
  padding: '4px 6px 4px 8px',
  justifyContent: 'space-between',
  flexShrink: 0,
  alignItems: 'center',
  borderRadius: '4px',
  fontFamily: 'Noto Sans JP',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '100%',
  boxShadow: `0px 1px 2px 0px ${colorPalette.monotone[4]}4D`,
  cursor: 'pointer',
}

// 軽量なSVGアイコン（MUIアイコンの代わり）
const ExpandMoreIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ color: colorPalette.monotone[4] }}
  >
    <path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6z" />
  </svg>
)

const MoreVertIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill={colorPalette.monotone[4]}
    className="sevend-ag-cell-status__status-change-popper-trigger"
  >
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
)

type StatusCellRendererParams = ICellRendererParams & {
  data: ProjectPlanNewRow
}

// 軽量なステータスボタン（MUI Buttonの代わり）
const LightweightStatusButton = memo(
  ({ value, onClick }: { value: string; onClick: () => void }) => {
    const statusStyle = statusStyles[value] || defaultStatusStyle
    const buttonStyle = { ...statusButtonBaseStyle, ...statusStyle }

    return (
      <button type="button" style={buttonStyle} onClick={onClick}>
        {getWbsItemStatusLabel(value as WbsItemStatus)}
        <ExpandMoreIcon />
      </button>
    )
  },
  (prev, next) => prev.value === next.value
)

// カスタム比較関数: 実際に描画に影響する値のみを比較
const arePropsEqual = (
  prevProps: StatusCellRendererParams,
  nextProps: StatusCellRendererParams
): boolean => {
  // valueが変わったら再レンダリング
  if (prevProps.value !== nextProps.value) return false

  // wbsItemの参照を取得
  const prevWbsItem = prevProps.data?.body?.wbsItem ?? prevProps.data?.wbsItem
  const nextWbsItem = nextProps.data?.body?.wbsItem ?? nextProps.data?.wbsItem

  // wbsItemが両方nullish
  if (!prevWbsItem && !nextWbsItem) return true
  // 片方だけnullish
  if (!prevWbsItem || !nextWbsItem) return false

  // wbsItemの描画に関係するプロパティを比較
  if (prevWbsItem.status !== nextWbsItem.status) return false
  if (prevWbsItem.substatus !== nextWbsItem.substatus) return false

  // ProgressIconの遅延判定に影響する日付を比較
  if (
    prevWbsItem.scheduledDate?.startDate !==
    nextWbsItem.scheduledDate?.startDate
  ) {
    return false
  }
  if (
    prevWbsItem.scheduledDate?.endDate !== nextWbsItem.scheduledDate?.endDate
  ) {
    return false
  }
  if (prevWbsItem.actualDate?.startDate !== nextWbsItem.actualDate?.startDate) {
    return false
  }
  if (prevWbsItem.actualDate?.endDate !== nextWbsItem.actualDate?.endDate) {
    return false
  }

  return true
}

// React.memoでラップして直接エクスポート
export const ProjectPlanNewStatusCellRenderer = memo(
  (params: StatusCellRendererParams) => {
    const { value, data, api, node, colDef } = params
    const wbsItem = data?.body?.wbsItem ?? data?.wbsItem

    // onClickハンドラをuseCallbackでメモ化
    const handleClick = useCallback(() => {
      api.startEditingCell({
        rowIndex: node.rowIndex ?? 0,
        colKey: colDef?.colId ?? colDef?.field ?? '',
      })
    }, [api, node.rowIndex, colDef?.colId, colDef?.field])

    if (!wbsItem) {
      return null
    }

    return (
      <div className="sevend-ag-cell" style={rootStyle}>
        <span style={progressIconAreaStyle}>
          <ProgressIcon wbsItem={wbsItem} fontSize={20} />
        </span>
        <LightweightStatusButton value={value} onClick={handleClick} />
        <button type="button" style={iconButtonStyle}>
          <MoreVertIcon />
        </button>
      </div>
    )
  },
  arePropsEqual
)

// displayNameを設定（WDYRでのデバッグ用）
ProjectPlanNewStatusCellRenderer.displayName =
  'ProjectPlanNewStatusCellRenderer'
